

import Invoice from '../../common/models/Invoice.ts';
import Driver from '../types/Driver.ts';
import PdfPageWithText from '../types/PdfPageWithText.ts';
import PdfProcessedPage from '../types/PdfProcessedPage.ts';
import PdfTextTest, { StringAtIndexTest } from '../types/PdfTextTest.ts';
import { compute_pdf_line_items, compute_pdf_tests } from '../pdf_parser.ts';
import { stamp } from '../pdf_writer.ts';
import PdfLineItem from '../types/PdfLineItem.ts';

export default class extends Driver {
    constructor() {
        super()
    }

    parse_project_NaN(pdf_text_contents: string[]): string {
        const tests = [
            {
                "string": "ORDER",
                "elements_to_skip": 2
            } as StringAtIndexTest,
            {
                "string": "NO.",
                "elements_to_skip": 4
            } as StringAtIndexTest,
        ]

        let pdf_tests = {
            "first_string": 'CUSTOMER',
            "tests": tests,
            "elements_to_skip_to_return": 6
        } as PdfTextTest
    
        return compute_pdf_tests(pdf_text_contents, pdf_tests) ?? ""
    }
    
    parse_invoice_NaN(pdf_text_contents: string[]): string {
        let pdf_tests = {
            "first_string": 'INVOICE',
            "tests": [],
            "elements_to_skip_to_return": 4
        } as PdfTextTest
    
        return compute_pdf_tests(pdf_text_contents, pdf_tests) ?? ""
    }
    
    parse_total(pdf_text_contents: string[]): number | null {
        const tests = [
            {
                "string": "DUE",
                "elements_to_skip": 2
            } as StringAtIndexTest,
        ]

        let pdf_tests = {
            "first_string": 'TOTAL',
            "tests": tests,
            "elements_to_skip_to_return": 4
        } as PdfTextTest
    
        const total = compute_pdf_tests(pdf_text_contents, pdf_tests)
    
        return this.parse_money_string(total) ?? null
    }

    parse_invoice_line_items(pdf_text_contents: string[]): PdfLineItem[] | null {
        console.log('parse_invoice_line_items')
        const tests = [
            {
                "string": "ORDERED",
                "elements_to_skip": 1,
            }  as StringAtIndexTest,
        ]

        let pdf_tests = {
            "first_string": 'QTY',
            "tests": tests,
            "elements_to_skip_to_return": 2
        } as PdfTextTest

        const line_item_parser = function (text_contents: string[], starting_index: number) {
            let line_items = []
            let current_index_cursor = starting_index + 1
            
            let qty_ordered: string[] = []
            let qty_shipped: string[] = []
            let price: string[] = []
            let units_of_measure: string[] = []
            let price_total: string[] = []
            let part_names: string[] = []
            let descriptions: string[] = []

            let line_count = 0

            do {               
                qty_ordered.push(text_contents[current_index_cursor])
                current_index_cursor++
                line_count++
            } while (!(
                text_contents[current_index_cursor] == ""
                && text_contents[current_index_cursor + 1] == "PRODUCT"
                && text_contents[current_index_cursor + 2] == " "
                && text_contents[current_index_cursor + 3] == "CODE"
                && text_contents[current_index_cursor + 4] == ""
            ) && current_index_cursor < text_contents.length)
            
            current_index_cursor += 5

            let line_count_index = 0
            do {
                part_names.push(text_contents[current_index_cursor])
                line_count_index++
                current_index_cursor++
            } while (line_count_index < line_count)

            line_count_index = 0

            do {
                part_names[line_count_index] = part_names[line_count_index] + " " + text_contents[current_index_cursor]
                line_count_index++
                current_index_cursor++
            } while (line_count_index < line_count)

            line_count_index = 0

            current_index_cursor += 3

            let temp_description = ""
            do {
                if (text_contents[current_index_cursor + 1] == " ") {
                    temp_description += text_contents[current_index_cursor]
                    temp_description += text_contents[current_index_cursor + 1]
                    current_index_cursor += 2
                } else {
                    temp_description += text_contents[current_index_cursor]
                    descriptions.push(temp_description)
                    temp_description = ""
                    current_index_cursor++
                }
            } while (!(
                text_contents[current_index_cursor] == ""
                && text_contents[current_index_cursor + 1] == "QTY"
                && text_contents[current_index_cursor + 2] == "SHIPPED"
                && text_contents[current_index_cursor + 3] == ""
            ) && current_index_cursor < text_contents.length)

            current_index_cursor += 4

            do {
                qty_shipped.push(text_contents[current_index_cursor])
                current_index_cursor++
            } while (!(
                text_contents[current_index_cursor] == ""
                && text_contents[current_index_cursor + 1] == "PRICE"
                && text_contents[current_index_cursor + 2] == ""
            ) && current_index_cursor < text_contents.length)

            current_index_cursor += 3

            do {
                price.push(text_contents[current_index_cursor])
                current_index_cursor++
                line_count_index++
            } while (line_count_index < line_count)
            
            line_count_index = 0

            do {
                units_of_measure.push(text_contents[current_index_cursor])
                current_index_cursor++
            } while (!(
                text_contents[current_index_cursor] == ""
                && text_contents[current_index_cursor + 1] == "DISC."
            ) && current_index_cursor < text_contents.length)

            current_index_cursor += 2

            do {
                current_index_cursor++
            } while (
                !(
                text_contents[current_index_cursor] == "EXTENSION"
                && text_contents[current_index_cursor + 1] == ""
            ) && current_index_cursor < text_contents.length)

            current_index_cursor += 2
            
            do {
                price_total.push(text_contents[current_index_cursor])
                current_index_cursor++
                line_count_index++
            } while (line_count_index < line_count)
            
            line_count_index = 0

            while(line_count_index < line_count) {
                let new_line_item = {
                    "part_name": part_names[line_count_index],
                    "quantity": parseInt(qty_ordered[line_count_index]),
                    "quantity_shipped": parseInt(qty_shipped[line_count_index]),
                    "unit_of_measure": units_of_measure[line_count_index],
                    "price_per_unit": parseFloat(price[line_count_index]),
                    "line_item_total": parseFloat(price_total[line_count_index]),
                    "part_description": descriptions[line_count_index]
                } as PdfLineItem

                line_items.push(new_line_item)
                line_count_index++
            }

            return line_items
        }
    
        return compute_pdf_line_items(pdf_text_contents, pdf_tests, line_item_parser) ?? null
    }

    check(pdf_text_contents: string[]): boolean {
        const tests = [
            {
                "string": "E",
                "elements_to_skip": 2
            } as StringAtIndexTest,
            {
                "string": "53RD",
                "elements_to_skip": 4
            } as StringAtIndexTest,
            {
                "string": "STREET",
                "elements_to_skip": 6
            } as StringAtIndexTest,
            {
                "string": "SUITE",
                "elements_to_skip": 7
            } as StringAtIndexTest,
            {
                "string": "DAVENPORT,",
                "elements_to_skip": 10
            } as StringAtIndexTest,
            {
                "string": "IA",
                "elements_to_skip": 12
            } as StringAtIndexTest
        ]
        
        let pdf_tests = {
            "first_string": '937',
            "tests": tests,
            "elements_to_skip_to_return": 14
        } as PdfTextTest

        return compute_pdf_tests(pdf_text_contents, pdf_tests) === "52807"
    }

    parse_pages(pages: PdfPageWithText[]): PdfProcessedPage[] {
        return pages.map((page: PdfPageWithText) => {
            return new PdfProcessedPage({
                page_number: page.page_number,
                vendor: 'CED',
                line_items: this.parse_invoice_line_items(page.text_contents),
                project_NaN: this.parse_project_NaN(page.text_contents),
                invoice_NaN: this.parse_invoice_NaN(page.text_contents),
                total: this.parse_total(page.text_contents)
            })
        })
    }

    async stamp_invoices(invoices: Invoice[]): Promise<Invoice[]> {
        const x_coordinate = 150
        const y_coordinate = 789

        for (const index in invoices) {
            const invoice = invoices[index]
            if (invoice.pdf_bytes === null) {
                continue
            }

            const stamped_pdf_bytes = await stamp(x_coordinate, y_coordinate, invoice.invoice_information, invoice.pdf_bytes)
            invoices[index].stamped_pdf_bytes = stamped_pdf_bytes
            invoices[index].stamp_x = x_coordinate
            invoices[index].stamp_y = y_coordinate
        }

        return invoices 
    }
}